import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { t } from 'i18next';
import { getSenderAndRecipient, updatePayments } from '@/services/coloc-api/admin';
import { useGetOutgoingTransactionsListContext } from '../../components/outgoing-transactions-list/hooks/use-get-outgoing-transactions';
import { showToast } from '@/services/toast-service';
import { AxiosError } from 'axios';

const useUpdatePayments = () => {
  const {
    fetch: payoutsFetch,
    rowSelection,
    setRowSelection,
  } = useGetOutgoingTransactionsListContext()!;

  const arrayOfSelectedPaymentIds = useMemo(() => {
    return Object.keys(rowSelection).map((rowId) => Number(rowId));
  }, [rowSelection]);

  const mutationKey = 'mutateColocationPayments';

  const { mutateAsync, isLoading } = useMutation({
    mutationKey: [mutationKey],
    mutationFn: (paymentIds: number[]) => {
      return updatePayments({
        payment_ids: paymentIds,
      });
    },
    onSuccess: () => {
      payoutsFetch.refetch();
      showToast.success({
        text:
          t(
            'js.features.admin.transactions.outgoing_transactions_list.actions.success'
          ),
      });
      setRowSelection({});
    },
    onError: (error: unknown) => {
      const generic = t('js.features.admin.transactions.outgoing_transactions_list.actions.error');
      if (error instanceof AxiosError) {
        const data: any = error.response?.data;
        const messages = Array.isArray(data?.errors) ? data.errors : undefined;
        const message = messages?.length
          ? messages.join('\n')
          : typeof data?.error === 'string'
          ? data.error
          : generic;
        showToast.error({ text: message });
      } else {
        showToast.error({ text: generic });
      }
    },
  });

  const fetchPreview = async (paymentIds: number[]) => {
    try {
      const { data } = await getSenderAndRecipient({ payment_ids: paymentIds });
      return (data?.data ?? data) as unknown[];
    } catch (error) {
      const errorMessage = t(
        'js.features.admin.transactions.outgoing_transactions_list.actions.fetch_preview_error'
      );
      showToast.error({ text: errorMessage });
      return [];
    }
  };

  return {
    arrayOfSelectedPaymentIds,
    mutateAsync,
    isUpdatingPayments: isLoading,
    fetchPreview,
  };
};

export { useUpdatePayments };
